import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import classNames from 'classnames';

import { analytics } from '@/analytics/core/analytics';
import { deferEscapeKey } from '@/utils/deferEscapeKey';

import { ModalBody, ModalFooter, ModalHeader } from './ModalSections';
import type { ModalComponentProps, ModalSize } from './types';

import styles from './ModalComponent.module.scss';

export { ModalBody, ModalFooter, ModalFooterStart } from './ModalSections';
export type { ModalComponentProps, ModalSize } from './types';

const SIZE_CLASS: Record<ModalSize, string | undefined> = {
    sm: styles.sm,
    md: undefined,
    lg: styles.lg,
    xl: styles.xl,
    fullscreen: styles.xl,
};

/** Calls back once, when the dialog content mounts. */
function OnMount(props: { callback: () => void }) {
    const callbackRef = useRef(props.callback);
    callbackRef.current = props.callback;
    useEffect(() => callbackRef.current(), []);
    return null;
}

/** How long a static backdrop nudges the dialog when a click or Escape cannot close it. */
const NUDGE_MS = 100;

/**
 * The one modal in the portal, on Radix's Dialog: it portals to the body, traps focus and
 * hands it back to whatever opened it, locks the page scroll, and hides the page from
 * assistive technology while open. The overlay is the scroll container, so a dialog taller
 * than the window scrolls as a whole.
 *
 * `size="fullscreen"` is the xl dialog kept at least 90% of the window tall, for detail
 * views with tabs and dense content.
 */
export default function ModalComponent(props: ModalComponentProps) {
    const {
        isOpen,
        onClose,
        dismissible = true,
        title,
        titleIcon,
        closeTrackingId,
        size = 'md',
        className,
        contentClassName,
        backdrop = true,
        keyboard = true,
        centered = true,
        scrollable = false,
        onOpened,
        onClosed,
        bare = false,
    } = props;

    // Radix's own ids hold colons, which jsdom's selector engine chokes on in tests.
    const baseId = useId().replace(/:/g, '');
    const contentRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const nudgeTimer = useRef<number | undefined>(undefined);
    const [nudging, setNudging] = useState(false);
    const canDismiss = dismissible && onClose !== undefined;

    // Taken before the dialog mounts and moves focus, so closing can hand focus back.
    useLayoutEffect(() => {
        if (isOpen && document.activeElement instanceof HTMLElement) {
            openerRef.current = document.activeElement;
        }
    }, [isOpen]);

    useEffect(() => () => window.clearTimeout(nudgeTimer.current), []);

    function nudge() {
        window.clearTimeout(nudgeTimer.current);
        setNudging(true);
        nudgeTimer.current = window.setTimeout(() => setNudging(false), NUDGE_MS);
    }

    function handleHeaderClose() {
        if (closeTrackingId) {
            analytics.buttonClicked({
                trackingId: closeTrackingId,
                labelSource: 'explicit',
                buttonVariant: 'tertiary',
            });
        }
        onClose?.();
    }

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(open) => {
                if (!open && canDismiss) onClose?.();
            }}>
            <Dialog.Portal>
                <Dialog.Overlay
                    data-modal-part="overlay"
                    className={classNames(styles.overlay, {
                        [styles.noBackdrop]: backdrop === false,
                    })}>
                    <div
                        data-modal-part="shell"
                        className={classNames(
                            styles.shell,
                            SIZE_CLASS[size],
                            {
                                [styles.chrome]: !bare,
                                [styles.centered]: centered,
                                [styles.scrollable]: scrollable,
                                [styles.fullscreen]: size === 'fullscreen',
                                [styles.nudge]: nudging,
                            },
                            className
                        )}>
                        {/* Mounted for as long as the overlay, so the dialog fades out with it. */}
                        <Dialog.Content
                            ref={contentRef}
                            forceMount
                            id={`modal-${baseId}`}
                            aria-labelledby={
                                title !== undefined ? `modal-title-${baseId}` : undefined
                            }
                            aria-label={title === undefined ? props['aria-label'] : undefined}
                            aria-describedby={undefined}
                            className={classNames(styles.content, contentClassName)}
                            onOpenAutoFocus={(event) => {
                                // Focus lands on the dialog itself, as it always has, not on
                                // the header close button that comes first.
                                event.preventDefault();
                                (props.initialFocusRef?.current ?? contentRef.current)?.focus({
                                    preventScroll: true,
                                });
                            }}
                            onCloseAutoFocus={(event) => {
                                event.preventDefault();
                                const opener = openerRef.current;
                                openerRef.current = null;
                                if (opener?.isConnected) opener.focus({ preventScroll: true });
                                onClosed?.();
                            }}
                            onEscapeKeyDown={(event) => {
                                if (keyboard && canDismiss) {
                                    deferEscapeKey(event, () => onClose?.());
                                    return;
                                }
                                event.preventDefault();
                                if (backdrop === 'static' && canDismiss) nudge();
                            }}
                            onPointerDownOutside={(event) => {
                                if (backdrop === true && canDismiss) return;
                                event.preventDefault();
                                if (backdrop === 'static') nudge();
                            }}>
                            {onOpened ? <OnMount callback={onOpened} /> : null}
                            {title !== undefined && (
                                <ModalHeader
                                    titleId={`modal-title-${baseId}`}
                                    title={title}
                                    titleIcon={titleIcon}
                                    onClose={onClose ? handleHeaderClose : undefined}
                                />
                            )}
                            {props.children !== undefined ? (
                                props.children
                            ) : (
                                <>
                                    <ModalBody className={props.bodyClassName}>
                                        {props.body}
                                    </ModalBody>
                                    {props.footer !== undefined && (
                                        <ModalFooter className={props.footerClassName}>
                                            {props.footer}
                                        </ModalFooter>
                                    )}
                                </>
                            )}
                        </Dialog.Content>
                    </div>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
